import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { FaSave, FaSpinner, FaTimes } from 'react-icons/fa';
import { CloseIcon, SettingsShippingIcon } from 'ui-component/custom-icons/icons';
import toast from 'react-hot-toast';
import useIsMobile from 'utils/isMobileUtils';

const ShippingCompanyModal = ({ openPopup, details, handleClose, handleReloadList }) => {
    const [information, setInformation] = useState({ id: '', name: '', link: '', apiKey: '', apiUrl: '' });
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [isFormProcessing, setIsFormProcessing] = useState(false);
    const isMobile = useIsMobile();

    useEffect(() => {
        setInformation({
            id: details?.id || '',
            name: details?.name || '',
            apiKey: details?.apiKey || '',
            apiUrl: details?.apiUrl || '',
            link: details?.link || '',
        });
        setIsDataLoaded(true);
        // eslint-disable-next-line
    }, [details, openPopup]);

    const handleProcessSubmit = () => {
        setIsFormProcessing(true);
        if (!information?.name) {
            toast.error('Shipping company name is required');
            setIsFormProcessing(false);
            return;
        } else if (!information?.link) {
            toast.error('Shipping company link is required');
            setIsFormProcessing(false);
            return;
        }
        if (!information?.id) {
            toast.promise(
                api.settingsCreateShippingCompany(information).then((res) => {
                    if (res.status) handleReloadList();
                    return res?.status ? res : null;
                }),
                {
                    loading: 'Creating shipping company...',
                    success: (data) => `${data.message}`,
                    error: <b>Creating shipping company Failed</b>
                }
            ).finally(() => setIsFormProcessing(false));
        } else {
            toast.promise(
                api.settingsUpdateShippingCompany(information).then((res) => {
                    if (res.status) handleReloadList();
                    return res?.status ? res : null;
                }),
                {
                    loading: 'Updating shipping company...',
                    success: (data) => `${data.message}`,
                    error: <b>Updating shipping company failed</b>
                }
            ).finally(() => setIsFormProcessing(false));
        }
    };

    const handleProcessEnter = (event) => {
        if (event.key === 'Enter') {
            handleProcessSubmit();
        }
    };

    if (!openPopup) return null;

    if (isMobile) {
        return (
            <div
                className="d-flex"
                style={{
                    width: '100vw',
                    height: '100dvh',
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    zIndex: 20,
                    background: 'rgba(0,0,0,0.07)',
                    justifyContent: 'center',
                    alignItems: 'end',
                    paddingBottom: '90px',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)'
                }}
            >
                <div
                    className="popup shippingcompany-popup-mobile d-md-none"
                    style={{
                        position: 'relative',
                        transform: 'none',
                        top: 0,
                        left: 0,
                        maxWidth: '90%',
                        background: '#fff',
                        borderRadius: 12,
                        boxShadow: 'rgb(0 0 0 / 37%) 0px 0px 20px',
                        width: '100%',
                        padding: '24px 16px 0 16px',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <SettingsShippingIcon height={'36px'} width={'36px'} />
                            <span style={{ fontWeight: 600, fontSize: 18 }}>Shipping Company</span>
                        </div>
                        <button className="btn modal-close-icon" onClick={handleClose} style={{ background: 'none', border: 'none', padding: 0 }}>
                            <FaTimes size={18} />
                        </button>
                    </div>
                    <div style={{ color: '#888', fontSize: 14, marginBottom: 18 }}>
                        Manage your shipping company information
                    </div>
                    <div className="form-group" style={{ marginBottom: 18 }}>
                        <input
                            type="text"
                            className="form-control"
                            value={information?.name}
                            placeholder="Enter shipping company name"
                            onChange={(e) => setInformation({ ...information, name: e.target.value })}
                            onKeyDown={handleProcessEnter}
                        />
                    </div>
                    <div className="form-group" style={{ marginBottom: 18 }}>
                        <input
                            type="text"
                            className="form-control"
                            value={information?.link}
                            placeholder="Enter shipping link"
                            onChange={(e) => setInformation({ ...information, link: e.target.value })}
                            onKeyDown={handleProcessEnter}
                        />
                    </div>
                    <div className="form-group" style={{ marginBottom: 18 }}>
                        <input
                            type="text"
                            className="form-control"
                            value={information?.apiKey}
                            placeholder="Enter shipping API key"
                            onChange={(e) => setInformation({ ...information, apiKey: e.target.value })}
                            onKeyDown={handleProcessEnter}
                        />
                    </div>
                    <div className="form-group" style={{ marginBottom: 18 }}>
                        <input
                            type="text"
                            className="form-control"
                            value={information?.apiUrl}
                            placeholder="Enter shipping API URL"
                            onChange={(e) => setInformation({ ...information, apiUrl: e.target.value })}
                            onKeyDown={handleProcessEnter}
                        />
                    </div>
                    {/* Sticky bottom buttons */}
                    <div
                        className="btnarea"
                        style={{
                            position: 'sticky',
                            bottom: 0,
                            left: 0,
                            width: '100%',
                            background: '#fff',
                            padding: '16px 0 12px 0',
                            zIndex: 30,
                            display: 'flex',
                            gap: 10,
                            justifyContent: 'center',
                            marginTop: 'auto'
                        }}
                    >
                        <button
                            className="btn btn-main"
                            disabled={!isDataLoaded || isFormProcessing}
                            onClick={() => {
                                if (!isFormProcessing) handleProcessSubmit();
                            }}
                        >
                            {isFormProcessing ? (
                                <>
                                    <FaSpinner className="fa-spin" /> Saving...
                                </>
                            ) : (
                                <>
                                    <FaSave /> Save
                                </>
                            )}
                        </button>
                        <button className="btn new-btn-error" onClick={handleClose}>
                            <FaTimes /> Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Desktop version (Modal)
    return (
        <Modal show={openPopup} centered size="md">
            <Modal.Body>
                <div className="p-2">
                    <div style={{ padding: '20px 0 0 0' }}>
                        <div className="mb-2 ml-0 pl-0">
                            <div className="row">
                                <div className="col-12 text-center color-theme">
                                    <SettingsShippingIcon height={'60px'} width={'60px'} />
                                </div>
                            </div>
                            <button className="btn modal-close-icon" onClick={handleClose}>
                                <CloseIcon height={'16px'} width={'16px'} />
                            </button>
                        </div>
                        <div className="row align-items-top">
                            <div className="col-12 text-center " style={{ padding: '10px 0 20px 0' }}>
                                <div style={{ fontSize: '24px' }}>
                                    Shipping Company
                                </div>
                                <div className='textGray'>
                                    Manage your shipping company information
                                </div>
                            </div>
                        </div>
                        <div className="row align-items-top" style={{ padding: '3px 0' }}>
                            <div className="col-12">
                                <label htmlFor="inputName" className="form-label">Shipping Company Name</label>
                            </div>
                            <div className="col-12 form-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    id="inputName"
                                    value={information?.name}
                                    placeholder="Enter shipping company name"
                                    onChange={(e) => {
                                        setInformation({
                                            ...information,
                                            name: e.target.value
                                        })
                                    }}
                                />
                            </div>
                        </div>
                        <div className="row align-items-top" style={{ padding: '3px 0' }}>
                            <div className="col-12">
                                <label htmlFor="inputLink" className="form-label">Shipping Link</label>
                            </div>
                            <div className="col-12 form-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    id="inputLink"
                                    value={information?.link}
                                    placeholder="Enter shipping link"
                                    onChange={(e) => {
                                        setInformation({
                                            ...information,
                                            link: e.target.value
                                        })
                                    }}
                                />
                            </div>
                        </div>
                        <div className="row align-items-top" style={{ padding: '3px 0' }}>
                            <div className="col-12">
                                <label htmlFor="inputApiKey" className="form-label">API Key</label>
                            </div>
                            <div className="col-12 form-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    id="inputApiKey"
                                    value={information?.apiKey}
                                    placeholder="Enter API key"
                                    onChange={(e) => {
                                        setInformation({
                                            ...information,
                                            apiKey: e.target.value
                                        })
                                    }}
                                />
                            </div>
                        </div>
                        <div className="row align-items-top" style={{ padding: '3px 0' }}>
                            <div className="col-12">
                                <label htmlFor="inputApiUrl" className="form-label">API URL</label>
                            </div>
                            <div className="col-12 form-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    id="inputApiUrl"
                                    value={information?.apiUrl}
                                    placeholder="Enter API URL"
                                    onChange={(e) => {
                                        setInformation({
                                            ...information,
                                            apiUrl: e.target.value
                                        })
                                    }}
                                />
                            </div>
                        </div>
                        <div className="row align-items-top mt10" style={{ padding: '3px 0' }}>
                            <div className="col-12">
                                <button
                                    type="button"
                                    className="btn-block btn-primary btn-outlined-main-primary  white-var"
                                    disabled={!isDataLoaded || isFormProcessing}
                                    onClick={() => {
                                        if (!isFormProcessing) {
                                            handleProcessSubmit()
                                        }
                                    }}
                                    style={{ lineHeight: '25px' }}
                                >
                                    {
                                        isFormProcessing ? (
                                            <>
                                                <FaSpinner className="fa-spin" /> Saving...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave /> Save
                                            </>
                                        )
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default ShippingCompanyModal;
